/**
 * Process Item Drawer Component
 * 
 * Complete drawer solution for adding/editing processes including:
 * - Form component with process details
 * - Search mode for selecting process owner
 * - Automatic process code assignment
 * - Form validation
 * 
 * File Upload Flow:
 * 1. User selects files → Stored as File objects in local state (no upload yet)
 * 2. When form is submitted:
 *    - ADD MODE: Upload all selected files to get fileIds
 *    - EDIT MODE: 
 *      - Compare current files with initial files
 *      - Upload new files to get fileIds
 *      - Delete removed files from backend
 *      - Combine fileIds (existing + new)
 * 3. Pass documentIds and testSheetId to onSubmit
 * 4. This prevents:
 *    - Unnecessary uploads if user cancels
 *    - Data loss from premature deletions
 */

import React, { useState, useEffect } from 'react';
import { Form, Input, Button} from 'antd';
import styled from 'styled-components';
import RightContentDrawer from '../../../components/common/RightContentDrawer';

import ButtonGroup from '../../../components/common/ButtonGroup';

// ========== TYPE DEFINITIONS ==========

export interface CompanyFormData {
    companyId?: string; // Optional id for edit mode
    companyName?: string | null;
}

export interface ProcessItemDrawerProps {
    open: boolean;
    initialValues?: Partial<CompanyFormData>;
    nextProcessCode?: string; // For add mode: the next process code (A01, A02...)
    autoEnterSearchMode?: boolean; // Auto enter search mode on open (for missing owner)
    onClose: () => void;
    onSubmit: (values: CompanyFormData) => Promise<void>;
}

// ========== STYLED COMPONENTS ==========

const FormLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1F2937;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;

  .info-icon {
    color: #9CA3AF;
    font-size: 14px;
    cursor: help;

    &:hover {
      color: #6B7280;
    }
  }
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .ant-form-item {
    margin-bottom: 0 !important;
  }
` as typeof Form;

const StyledInput = styled(Input)`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  border-radius: 8px !important;
  letter-spacing: 0.005em;
  color: #6B7280;
  height: 44px;

  &.ant-input {
    color: #6B7280;
  }

  &::placeholder {
    color: #9CA3AF;
  }

  &:disabled {
    background: #F9FAFB;
    color: #9CA3AF;
    cursor: not-allowed;
  }
`;

const CharacterCounter = styled.div<{ $isNearLimit?: boolean }>`
  font-family: Inter;
  font-weight: 400;
  font-size: 12px;
  line-height: 150%;
  color: ${props => props.$isNearLimit ? '#EF4444' : '#9CA3AF'};
  margin-top: 4px;
  text-align: right;
`;

// ========== MAIN COMPONENT ==========

const EditCompanyDrawer: React.FC<ProcessItemDrawerProps> = ({
    open,
    initialValues,
    onClose,
    onSubmit,
}) => {
    // Form states
    const [form] = Form.useForm();
    const [companyName, setCompanyName] = useState('');

    // UI states
    const [loading, setLoading] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);

    // Reset form when initialValues change
    useEffect(() => {
        setCompanyName(initialValues?.companyName || '');
    }, [initialValues]);

    // Watch for field changes and validate
    useEffect(() => {
        const checkFormValidation = () => {
            const hasName = !!companyName && companyName.trim() !== '';

            const isValid = hasName;
            setIsFormValid(isValid);
        };

        checkFormValidation();
    }, [companyName]);

    const resetForm = () => {
        setCompanyName('');
        form.resetFields();
    };


    const handleClose = () => {
        setIsFormValid(false);
        resetForm();
        onClose();
    };

    // Handle drawer close button (X) - context-aware
    const handleDrawerClose = () => {

        handleClose();

    };

    const handleFormSubmit = async () => {
        setLoading(true);
        try {
            


            // Submit form with file IDs
            await onSubmit({
                companyId: initialValues?.companyId,
                companyName: companyName,
            });

            handleClose();
        } catch (error) {
            console.error("Form submission failed:", error);
        } finally {
            setLoading(false);
        }
    };

    // Render form UI
    const renderFormUI = () => (
        <StyledForm
            id="user-form"
            form={form}
            layout="vertical"
            requiredMark={false}
        >
            
            <Form.Item name="company">
                {
                <div>
                    <FormLabel>Company Name</FormLabel>
                    <StyledInput
                        value={companyName}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Enforce maxLength of 100 characters
                            if (value.length <= 100) {
                                setCompanyName(value);
                            }
                        }}
                        placeholder="Enter company name"
                        maxLength={100}
                        spellCheck={true}
                    />
                    <CharacterCounter $isNearLimit={companyName.length >= 90}>
                        {companyName.length}/100
                    </CharacterCounter>
                </div>}
            </Form.Item>
        </StyledForm>
    );

    const drawerTitle = 'Edit Company';

    return (
        <RightContentDrawer
            open={open}
            title={drawerTitle}
            width={572}
            onClose={handleDrawerClose}
            footer={
                (
                    // Normal mode footer: Go back + Submit buttons
                    <ButtonGroup>
                        <Button type="default" onClick={handleClose} disabled={loading}>
                            Go back
                        </Button>
                        <Button
                            type="primary"
                            loading={loading}
                            disabled={!isFormValid}
                            form="user-form"
                            onClick={handleFormSubmit}
                        >
                            {'Save changes'}
                        </Button>
                    </ButtonGroup>
                )
            }
        >
            {renderFormUI()}
        </RightContentDrawer>
    );
};

export default EditCompanyDrawer;

